import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Bookmark, ListChecks, Sparkles, TrendingUp, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { useT } from "@/i18n/LanguageContext";

const KpiCard = ({ icon: Icon, label, value, testid, tint }) => (
  <motion.div whileHover={{ y: -2 }} className="rounded-xl border bg-card p-5 shadow-sm transition-colors">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tint}`}><Icon className="h-4 w-4" /></span>
    </div>
    <div className="mt-3 font-heading text-3xl font-bold tabular-nums" data-testid={testid}>{value}</div>
  </motion.div>
);

const STATUS_COLORS = {
  New: "bg-slate-400", Contacted: "bg-cyan-500", Replied: "bg-amber-500",
  Interested: "bg-emerald-500", Won: "bg-emerald-700", Lost: "bg-rose-500",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const t = useT();
  const [stats, setStats] = React.useState(null);
  const [quick, setQuick] = React.useState("");
  const [recent, setRecent] = React.useState([]);

  React.useEffect(() => {
    api.stats().then(setStats).catch(() => {});
    api.getLeads({ limit: 8 }).then((d) => setRecent(d.leads || [])).catch(() => {});
  }, []);

  const goSearch = () => {
    if (quick.trim()) navigate("/search", { state: { query: quick } });
    else navigate("/search");
  };

  const s = stats || { total_leads: 0, saved_leads: 0, lists_count: 0, status_counts: {}, sub_industry_counts: [] };
  const totalStatus = Object.values(s.status_counts || {}).reduce((a, b) => a + b, 0) || 1;
  const maxSub = Math.max(1, ...(s.sub_industry_counts || []).map((x) => x.count));

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl border bg-card p-6 shadow-sm">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(1000px_circle_at_20%_0%,hsla(196,78%,34%,0.13),transparent_55%),radial-gradient(800px_circle_at_90%_10%,hsla(38,92%,55%,0.10),transparent_50%)]" />
        <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="mb-1 flex items-center gap-2 text-sm font-medium text-primary"><Sparkles className="h-4 w-4" /> {t("dash_welcome")}</div>
            <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{t("dash_title")}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t("dash_subtitle")}</p>
          </div>
          <Button onClick={() => navigate("/search")} data-testid="dashboard-go-search-button">
            {t("dash_new_search")} <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard icon={Users} label={t("kpi_total")} value={s.total_leads} testid="kpi-total-leads" tint="bg-primary/10 text-primary" />
        <KpiCard icon={Bookmark} label={t("kpi_saved")} value={s.saved_leads} testid="kpi-saved-leads" tint="bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" />
        <KpiCard icon={ListChecks} label={t("kpi_lists")} value={s.lists_count} testid="kpi-lists" tint="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" />
        <KpiCard icon={TrendingUp} label={t("kpi_pipeline")} value={(s.status_counts?.Contacted || 0) + (s.status_counts?.Replied || 0) + (s.status_counts?.Interested || 0)} testid="kpi-pipeline" tint="bg-cyan-100 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <div className="mb-3 flex items-center gap-2 font-heading font-semibold"><Sparkles className="h-4 w-4 text-primary" /> {t("quick_search")}</div>
          <Textarea value={quick} onChange={(e) => setQuick(e.target.value)} placeholder={t("quick_search_placeholder")} className="min-h-[80px] resize-none" data-testid="dashboard-quick-search-input" />
          <Button className="mt-3 w-full" onClick={goSearch} data-testid="dashboard-quick-search-button">{t("search_leads")}</Button>
        </Card>

        <Card className="p-5" data-testid="kpi-status-breakdown">
          <div className="mb-4 font-heading font-semibold">{t("outreach_pipeline")}</div>
          <div className="space-y-3">
            {["New", "Contacted", "Replied", "Interested", "Won", "Lost"].map((st) => {
              const c = s.status_counts?.[st] || 0;
              return (
                <div key={st} className="flex items-center gap-3">
                  <div className="w-24"><StatusBadge status={st} /></div>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                    <div className={`h-full rounded-full ${STATUS_COLORS[st]}`} style={{ width: `${(c / totalStatus) * 100}%` }} />
                  </div>
                  <span className="w-8 text-right text-sm font-medium tabular-nums">{c}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <div className="mb-4 font-heading font-semibold">{t("leads_by_subindustry")}</div>
        {(s.sub_industry_counts || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("no_data_yet")}</p>
        ) : (
          <div className="space-y-3">
            {s.sub_industry_counts.map((row) => (
              <div key={row.sub_industry} className="flex items-center gap-3">
                <div className="w-40 truncate text-sm">{row.sub_industry}</div>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${(row.count / maxSub) * 100}%` }} />
                </div>
                <span className="w-8 text-right text-sm font-medium tabular-nums">{row.count}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="font-heading font-semibold">{t("recent_contacts")}</div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/search")} data-testid="dashboard-view-all-button">
            {t("view_all")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>
        {recent.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("recent_empty")}</p>
        ) : (
          <div className="divide-y">
            {recent.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{l.full_name} <span className="font-normal text-muted-foreground">· {l.job_title}</span></div>
                  <div className="truncate text-xs text-muted-foreground">{l.company_name} · {l.location}</div>
                </div>
                <StatusBadge status={l.status} />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
